import { Link } from "react-router-dom";
import { Phone, ClipboardList, Tags, Megaphone, Sparkles } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { CtaBand } from "../components/CtaBand";
import { SITE, IMAGES } from "../data/site";

const PROCESS = [
  {
    icon: ClipboardList,
    title: "Walkthrough & consultation",
    text: "We meet you at the property, look at everything together, and talk honestly about what will sell and how. You receive a clear written quote — no pressure, no obligation.",
  },
  {
    icon: Tags,
    title: "Sorting, staging & pricing",
    text: "Our team organizes the home, researches and prices each item fairly, and stages everything so buyers see it at its best.",
  },
  {
    icon: Megaphone,
    title: "Advertising & sale days",
    text: "We promote the sale to serious buyers — including listing on Estatesales.net — then staff and run the sale itself, start to finish.",
  },
  {
    icon: Sparkles,
    title: "After the sale",
    text: "You get a clear accounting of what sold. Then, if you'd like, we handle donations and the final cleanout so the property is ready for what's next.",
  },
];

const WHO = [
  "Families settling the estate of a parent or loved one",
  "Executors and trustees who need a reliable local team",
  "Homeowners downsizing who want belongings sold, not stored",
  "Attorneys and agents coordinating on behalf of a client",
];

const SITUATIONS = [
  "A full house that needs to be emptied on a timeline",
  "Managing everything from out of state",
  "Mixed contents — everyday items alongside antiques and collections",
  "A sale followed by a cleanout before listing the home",
];

export default function EstateSalesPage() {
  usePageMeta(
    "Estate Sales | MacWay Estate Services — DC, MD & VA",
    "Full-service estate sales across DC, Maryland, and Virginia: sorting, staging, pricing, advertising on Estatesales.net, hosting, and post-sale cleanout. Call (301) 500-0128."
  );

  return (
    <main data-testid="estate-sales-page">
      <section className="relative py-24 md:py-32 flex items-center" data-testid="estate-sales-hero">
        <img src={IMAGES.estateSale} alt="A well-appointed living room prepared for an estate sale" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/60 to-ink/30" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <p className="eyebrow-label">Estate sales</p>
            <h1 className="mt-4 text-5xl sm:text-6xl font-semibold tracking-tight text-ivory leading-[1.05]" data-testid="estate-sales-headline">
              A sale handled with care, from first sort to final accounting
            </h1>
            <p className="mt-6 text-lg text-ivory/85 leading-relaxed">
              An estate sale is more than a transaction — it's a house full of someone's life. MacWay manages the
              entire process so your family gets fair value without the exhaustion.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-brass" data-testid="estate-sales-quote-cta">Request a Quote</Link>
              <a href={SITE.phoneHref} className="btn-outline-light" data-testid="estate-sales-call-cta">
                <Phone className="w-4 h-4" /> Call {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory" data-testid="estate-sales-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">Our process</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">How a MacWay estate sale works</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {PROCESS.map((p) => (
              <div key={p.title} className="bg-white border border-linen p-8">
                <p.icon className="w-8 h-8 text-brass" />
                <h3 className="mt-5 text-2xl font-medium text-ink">{p.title}</h3>
                <p className="mt-3 text-base text-ink/70 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-sand" data-testid="estate-sales-who">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="eyebrow-label">Who it helps</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-medium tracking-tight text-ink">Made for moments like these</h2>
            <ul className="mt-7 space-y-4">
              {WHO.map((w) => (
                <li key={w} className="flex items-start gap-3 text-base text-ink/75">
                  <span className="mt-1.5 w-2 h-2 bg-brass shrink-0" aria-hidden="true" />
                  {w}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="eyebrow-label">Common situations</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-medium tracking-tight text-ink">If any of this sounds familiar</h2>
            <ul className="mt-7 space-y-4">
              {SITUATIONS.map((s) => (
                <li key={s} className="flex items-start gap-3 text-base text-ink/75">
                  <span className="mt-1.5 w-2 h-2 bg-forest shrink-0" aria-hidden="true" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <CtaBand
        title="Let's talk about the house."
        text="A short conversation is all it takes to learn whether an estate sale is the right fit — and what it could look like for your family."
      />
    </main>
  );
}
